/**
 * Voice-utterances QC panel — renders the audio archive for a saved
 * ball so an analyst can play back exactly what was said vs what the
 * SPA actually scored. Surfaces systematic STT errors that no unit
 * test catches.
 *
 * Founder ask 2026-05-26 ("audio attached to every ball, post-match
 * QC can listen to what was said vs what got coded").
 *
 * For each utterance:
 *   - <audio controls> with a fresh 1-hour signed S3 URL
 *   - Transcript Whisper returned
 *   - The parser's interpretation (label) if available
 *   - "I said …" correction field — saves an operator-supplied
 *     correct transcript, building labelled training data
 */
import { useEffect, useState } from "react";
import { Volume2, AlertTriangle, Check, Loader2 } from "lucide-react";
import api from "../../api/axios";


export default function VoiceUtterancesPanel({ ballId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [utterances, setUtterances] = useState([]);

  useEffect(() => {
    if (!ballId) return undefined;
    let alive = true;
    setLoading(true); setError(null);
    api.get(`/api/scoring/balls/${ballId}/voice-utterances/`)
      .then((res) => {
        if (!alive) return;
        setUtterances(res.data?.utterances || []);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.detail || e.message || "Failed to load voice audio");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [ballId]);

  if (loading) {
    return (
      <div className="px-5 py-4 text-sm text-text-secondary flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading voice audio…
      </div>
    );
  }
  if (error) {
    return (
      <div className="px-5 py-4 text-sm text-rose-300 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4" /> {error}
      </div>
    );
  }
  if (!utterances.length) {
    return (
      <div className="px-5 py-6 text-center text-sm text-text-secondary">
        No voice utterances recorded for this ball.<br />
        <span className="text-[11px] opacity-70">
          (Voice audio is captured only when the operator uses the 🎤
          PTT / 🎧 hands-free mic during scoring. Manual taps don't
          create utterance rows.)
        </span>
      </div>
    );
  }

  return (
    <div className="px-5 py-4 space-y-3">
      <p className="text-[11px] text-text-secondary">
        Each row is one voice utterance from when this ball was scored.
        Play the audio to verify the transcript captured what was said.
        If the transcript is wrong, type the correction below — that
        labelled pair feeds our STT improvement pipeline.
      </p>
      {utterances.map((u) => (
        <UtteranceRow key={u.id} u={u} />
      ))}
    </div>
  );
}


function UtteranceRow({ u }) {
  const [correction, setCorrection] = useState(u.operator_correction || "");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const submitCorrection = async () => {
    const trimmed = correction.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await api.post("/api/scoring/voice/correction/", {
        utterance_id: u.id,
        correction: trimmed,
      });
      setSavedAt(new Date());
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("correction save failed:", e?.response?.data || e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-md border border-bg-secondary bg-bg-main/50 p-3">
      <div className="flex items-start gap-3 mb-2">
        <Volume2 className="w-4 h-4 text-purple-300 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-1">
            {new Date(u.created_at).toLocaleTimeString()} · {u.stt_model}
            {u.confidence != null && (
              <> · confidence {Number(u.confidence).toFixed(2)}</>
            )}
          </div>
          <div className="text-sm text-text-primary">
            <span className="text-text-secondary mr-1">heard:</span>
            <span className="font-medium">{u.transcript || <em className="opacity-60">(empty transcript)</em>}</span>
          </div>
          {u.parsed_label && (
            <div className="text-xs text-text-secondary mt-1">
              <span className="opacity-70">parsed:</span> {u.parsed_label}
            </div>
          )}
        </div>
      </div>

      {u.audio_url && (
        <audio
          controls
          src={u.audio_url}
          className="w-full mb-2"
          preload="none"
        />
      )}

      <div className="flex items-start gap-2">
        <input
          type="text"
          value={correction}
          onChange={(e) => setCorrection(e.target.value)}
          placeholder='I actually said… (e.g. "four boundary")'
          className="flex-1 bg-bg-secondary border border-border-default rounded px-2 py-1.5 text-xs text-text-primary placeholder-text-secondary/60"
        />
        <button
          type="button"
          onClick={submitCorrection}
          disabled={saving || !correction.trim()}
          className="px-3 py-1.5 rounded text-[10px] uppercase tracking-wider bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
           savedAt ? <Check className="w-3.5 h-3.5" /> : null}
          Save correction
        </button>
      </div>
      {savedAt && (
        <div className="text-[10px] text-emerald-400 mt-1">
          Saved · feeds STT improvement
        </div>
      )}
    </div>
  );
}
